import { DateTime } from 'luxon';
import { type FC, useEffect, useRef, useState } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { BuildingGear, ExclamationTriangle } from 'react-bootstrap-icons';

import { useNotifications } from '../../../modules/hooks';
import { getApiErrorMessage } from '../../../modules/utils/transform';
import Header from '../../Header';
import { KpiRow } from './components/KpiRow';
import { OccupancyTrendCard } from './components/OccupancyTrendCard';
import { RatingsCard } from './components/RatingsCard';
import { SentimentCard } from './components/SentimentCard';
import { useDashboardReport } from './useDashboardReport';

import './styles/styles.css';

export const Dashboard: FC = () => {
  const { setSimpleToasts } = useNotifications() || {};

  // Sent explicitly rather than relying on the server's default, so the "today"
  // heading and the data cannot disagree if the server's timezone differs.
  const [date] = useState(() => DateTime.now().toFormat('yyyy-MM-dd'));

  const { data, loading, fetching, error, enabled, refetch } = useDashboardReport(date);

  const errorMessage = error ? getApiErrorMessage(error) : null;

  // One toast per distinct message — a plain effect would re-fire on re-render.
  const lastToastedError = useRef<string | null>(null);

  useEffect(() => {
    if (!errorMessage) {
      lastToastedError.current = null;
      return;
    }

    if (lastToastedError.current === errorMessage) {
      return;
    }

    lastToastedError.current = errorMessage;
    setSimpleToasts?.({ type: 'danger', message: errorMessage, show: true });
  }, [errorMessage, setSimpleToasts]);

  const todayLabel = DateTime.fromISO(date).toFormat('cccc, d LLLL yyyy');

  const renderBody = () => {
    if (!enabled) {
      return (
        <div className="dashboard-state">
          <span className="dashboard-state__icon">
            <BuildingGear size={32} />
          </span>
          <h2 className="dashboard-state__title">Select a property</h2>
          <p className="dashboard-state__text">
            Choose a property from the sidebar to see its occupancy, ratings and
            today&apos;s arrivals.
          </p>
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div className="dashboard-state">
          <span className="dashboard-state__icon">
            <ExclamationTriangle size={32} />
          </span>
          <h2 className="dashboard-state__title">Couldn&apos;t load the dashboard</h2>
          <p className="dashboard-state__text">{errorMessage}</p>
          <Button variant="primary" onClick={refetch}>
            Try again
          </Button>
        </div>
      );
    }

    return (
      <>
        {fetching && !loading && <p className="dashboard-page__status">Updating…</p>}

        <KpiRow arrivals={data?.arrivals} loading={loading} dateLabel={todayLabel} />

        <Row className="g-3 mt-0">
          <Col xs={12} xl={8}>
            <OccupancyTrendCard occupancy={data?.occupancy} loading={loading} />
          </Col>
          <Col xs={12} xl={4}>
            <RatingsCard ratings={data?.ratings} loading={loading} />
          </Col>
        </Row>

        <Row className="g-3 mt-0">
          <Col xs={12}>
            <SentimentCard sentiment={data?.sentiment} loading={loading} />
          </Col>
        </Row>
      </>
    );
  };

  return (
    <Container fluid className="dashboard-page p-0">
      <Header title="Dashboard" />
      {/*
        Gutters live on this inner wrapper, not the Container: the Container
        keeps `p-0` so Header lines up with every other page, and Bootstrap's
        .p-0 is !important so it would beat any padding set on the Container.
      */}
      <div className="dashboard-page__content">{renderBody()}</div>
    </Container>
  );
};
